import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { Workflow } from '@tasks/contracts';

import { queryKeys } from '../../api/queryKeys';
import { getWorkflow, getWorkflowById } from '../../api/workflow';

/**
 * Карта допустимых переходов `fromStatusId → Set<toStatusId>`.
 * Drop в недопустимую колонку отменяется до запроса (UC-BRD-02 AC2).
 *
 * Переходы принадлежат workflow доски, а не проекту: у типов задач бывают
 * разные наборы, и доска одного флоу не должна разрешать переходы другого.
 * Пока доска не загрузилась, читается workflow по умолчанию — он же и
 * отрисуется, если у доски своего набора нет.
 */
export function useAllowedTransitions(
  projectKey: string,
  workflowId?: string,
): {
  readonly map: ReadonlyMap<string, ReadonlySet<string>>;
  readonly workflow: Workflow | null;
} {
  const query = useQuery({
    queryKey:
      workflowId === undefined
        ? queryKeys.projects.workflow(projectKey)
        : queryKeys.projects.workflowById(projectKey, workflowId),
    queryFn: ({ signal }) =>
      workflowId === undefined
        ? getWorkflow(projectKey, signal)
        : getWorkflowById(projectKey, workflowId, signal),
    enabled: projectKey.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const map = useMemo(() => {
    const result = new Map<string, Set<string>>();
    for (const transition of query.data?.transitions ?? []) {
      const existing = result.get(transition.fromStatusId) ?? new Set<string>();
      existing.add(transition.toStatusId);
      result.set(transition.fromStatusId, existing);
    }
    return result as ReadonlyMap<string, ReadonlySet<string>>;
  }, [query.data]);

  return { map, workflow: query.data ?? null };
}

export function useTransitionMapRefresh(projectKey: string): () => void {
  const query = useQuery({
    queryKey: queryKeys.projects.workflow(projectKey),
    enabled: false,
    queryFn: () => Promise.resolve(null),
  });
  const client = useQueryClient();
  return useCallback(() => {
    void query.refetch();
    // Обновляем и конкретные workflow: их ключи вложены в корень «workflow»,
    // поэтому префиксная инвалидация захватывает и доску.
    void client.invalidateQueries({ queryKey: queryKeys.projects.workflow(projectKey) });
  }, [client, projectKey, query]);
}
